import { useCallback, useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import Table from 'src/components/table/table'
import Filters from 'src/components/table/filters'

import { useNotification } from 'src/contexts/notification.context'
import { useAuth } from 'src/contexts/auth.context'
import {
  getMyPrintables,
  deletePrintable,
  duplicatePrintable,
} from 'src/services/api.service'

import { routes } from 'src/navigation/routes'
import PrintableRow from './components/printable-row'

const statusOptions = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'in review', label: 'In review' },
  { value: 'active', label: 'Active' },
  { value: 'rejected', label: 'Rejected' },
]

const cacheKey = 'printables'

export const Shop = () => {
  const history = useHistory()
  const location = useLocation()
  const { user, setViewStates, cache, setCache } = useAuth()
  const { showErrorHelper, showNotificationHelper, showConfirm } =
    useNotification()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(
    cache[cacheKey]?.data || {
      items: [],
      count: 0,
      pageCount: 1,
    },
  )
  const [filter, setFilter] = useState(
    cache[cacheKey]?.filter || {
      subject_id: null,
      grade_id: null,
      status: null,
      period: null,
      query: '',
    },
  )
  const [lastFetchParams, setLastFetchParams] = useState({})

  const [step, setStep] = useState(0)
  const [agree, setAgree] = useState(false)

  useEffect(() => {
    setStep(location.state?.step || user?.view_states?.shopStep || 1)
  }, [user?.view_states?.shopStep])

  const fetchData = useCallback(async ({ pageSize, pageIndex, filterData }) => {
    try {
      setLastFetchParams({ pageSize, pageIndex, filterData })
      setLoading(true)
      const response = await getMyPrintables({
        ...filterData,
        page: pageIndex + 1,
        perPage: pageSize,
        my_only: 1,
      })
      const newData = {
        items: response.data.items,
        count: response.data.total,
        pageCount: Math.ceil(response.data.total / pageSize),
      }

      setData(newData)
      if (pageIndex === 0) {
        setCache({ [cacheKey]: { data: newData, filter: filterData } })
      }
    } catch (error) {
      showErrorHelper(error)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleContinue = (e) => {
    e.preventDefault()
    moveToNextStep(2)
  }

  const handleLearningAccept = (e) => {
    e.preventDefault()

    if (!agree) return

    moveToNextStep(3)
  }

  const handleSubmitMore = (e) => {
    e.preventDefault()
    moveToNextStep(3)
  }

  const moveToNextStep = (s) => {
    setViewStates({ shopStep: s })
    setStep(s)
  }

  // Row handlers
  const handleEditClick = (item) => () => {
    history.push(routes.printables.edit(item.id))
  }

  const handleDeleteClick = (item) => () => {
    showConfirm({
      title: 'Are you sure?',
      body: `Do you really want to delete the ${item.title}?`,
      actions: {
        onOk: async () => {
          await deletePrintable(item.id)
          showNotificationHelper({
            title: 'Success',
            body: 'Printable has been deleted',
          })
          fetchData(lastFetchParams)
        },
      },
    })
  }

  const handleDuplicateClick = (item) => async () => {
    await duplicatePrintable(item.id)
    fetchData(lastFetchParams)
  }

  return (
    <>
      {step === 1 && (
        <>
          <h2 className="sectionHb-title">My shop</h2>
          <div className="sectionHb-block authorsHb">
            <div className="sectionHb-block__wrap">
              <div className="authorsHb-info">
                <h2 className="sectionHb-title">Power to the authors!</h2>
                <div className="authorsHb-img">
                  <img src="/account/img/authors.svg" alt="" />
                </div>
                <p>
                  Welcome to your <b>Practithink</b> marketplace built to
                  support amazing creative educational material authorsHb like
                  you.
                </p>
                <p>
                  Photos uploaded by you will be distributed under{' '}
                  <a
                    rel="noreferrer"
                    target="_blank"
                    href={`${process.env.REACT_APP_API_HOST}/terms-of-use`}
                  >
                    Practithink license
                  </a>
                  . To improve your chances of publication, make sure your
                  content is meet{' '}
                  <a
                    rel="noreferrer"
                    target="_blank"
                    href={`${process.env.REACT_APP_API_HOST}/copyright-authors-agreement`}
                  >
                    {' '}
                    our requirements.
                  </a>
                </p>
                <div className="authorsHb-btn">
                  <a href="#" className="primary-btn" onClick={handleContinue}>
                    Continue
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h2 className="sectionHb-title">My shop</h2>
          <div className="sectionHb-block authorsHb authorsHb-big">
            <div className="sectionHb-block__wrap">
              <div className="authorsHb-info">
                <h2 className="sectionHb-title">Upload printable resources</h2>
                <div className="authorsHb-img">
                  <img src="/account/img/authors-2.svg" alt="" />
                </div>
                <ul className="authorsHb-list">
                  <li>Free or premium</li>
                  <li>Share your work</li>
                  <li>Interesting and age appropriate content</li>
                  <li>High-resolution materials</li>
                </ul>
                <p>
                  We will review your submissions. If your materials are
                  selected, you receive an email notification. After that, your
                  learning materials will be available on our marketplace
                </p>
                <div className="checkbox">
                  <input
                    type="checkbox"
                    checked={agree}
                    onClick={() => {
                      setAgree(!agree)
                    }}
                  />
                  <span className="checkbox-ico" />
                  <p>
                    <span>
                      I read and agree to Practithink copyright policy{' '}
                    </span>
                  </p>
                </div>
                <div className="authorsHb-btn">
                  <a
                    href="#"
                    className="primary-btn"
                    onClick={handleLearningAccept}
                  >
                    Learning Materials
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className="sectionHb-top sectionHb-top__space">
            <h2 className="sectionHb-title">My shop</h2>
            <div className="sectionHb-available">
              <div className="sectionHb-available__info">
                <span>Available</span>
                <b>$0.00</b>
              </div>
              <a href="#" className="second-btn">
                Transfer money
              </a>
            </div>
          </div>

          <div className="sectionHb-block purchaseHb">
            <div className="sectionHb-block__wrap">
              <Filters
                buttonComponent={
                  <Link
                    to={routes.printables.new}
                    className="primary-btn primary-btn__big"
                  >
                    <i className="hb-ico plus-ico-white" />
                    Printable
                  </Link>
                }
                filter={filter}
                setFilter={setFilter}
                status={{
                  options: statusOptions,
                }}
              />

              <div className="purchaseHb-table myShop-table">
                <div className="purchaseHb-table__row purchaseHb-table__top">
                  <div className="purchaseHb-table__col purchaseHb-table__col-1">
                    Product
                  </div>
                  <div className="purchaseHb-table__col purchaseHb-table__col-2">
                    Price
                  </div>
                  <div className="purchaseHb-table__col purchaseHb-table__col-3">
                    Sales
                  </div>
                  <div className="purchaseHb-table__col purchaseHb-table__col-4">
                    Status
                  </div>
                  <div className="purchaseHb-table__col purchaseHb-table__col-5">
                    Data
                  </div>
                </div>
                <Table
                  fetchData={fetchData}
                  data={data.items}
                  pageCount={data.pageCount}
                  filter={filter}
                  loading={loading}
                  rowComponent={(item) => (
                    <PrintableRow
                      key={item.id}
                      item={item}
                      onEdit={handleEditClick(item)}
                      onDelete={handleDeleteClick(item)}
                      onPrint={() => {}}
                      onCopy={handleDuplicateClick(item)}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {step === 4 && (
        <>
          <h2 className="sectionHb-title">My shop</h2>
          <div className="sectionHb-block authorsHb authorsHb-big">
            <div className="sectionHb-block__wrap">
              <div className="authorsHb-info">
                <h2 className="sectionHb-title">Way to go!</h2>
                <div className="authorsHb-img">
                  <img src="/account/img/authors-3.svg" alt="" />
                </div>
                <p>
                  Your learning materials are currently being reviewed by our
                  editing team and you’ll receive an email once they are done
                </p>
                <div className="authorsHb-btn">
                  <a
                    href="#"
                    onClick={handleSubmitMore}
                    className="primary-btn"
                  >
                    Submit more
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
